import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSoloArtistSubscription } from "@/hooks/useSoloArtistSubscription";
import { usePlatformAdmin } from "@/hooks/usePlatformAdmin";

/**
 * Subscription tiers for the Solo Artist family.
 *
 *   free        — no paid sub, free-tier limits apply
 *   booker      — $9/mo Solo Booker, booking-only cap set
 *   pro         — $19/mo Solo Pro, full single-artist app
 *   pro_plus    — $29/mo Solo Pro Plus, Solo Pro + 2 guest-artist seats
 *   shop_member — user is on a Studio plan via shop_memberships
 *
 * Detection lives in useSoloArtistSubscription. Tiers that don't yet
 * have a Stripe product (today: pro_plus + Booker annual/EU variants)
 * are dormant: the type slots exist so capability code is ready, but
 * detection never returns those values until real Stripe IDs are
 * wired up.
 */
export type SoloArtistTier = "free" | "booker" | "pro" | "pro_plus" | "shop_member";

// Pro-only features
export type ProFeature = "sms" | "ai_assistant" | "finance" | "pos" | "events";

const FREE_TIER_LIMITS = {
  appointmentsPerMonth: 10,
  totalClients: 20,
} as const;

export function useSoloArtistLimits() {
  const { user, memberships } = useAuth();
  const { tier: subTier } = useSoloArtistSubscription();
  const { isPlatformAdmin } = usePlatformAdmin();

  // Platform admins always get pro tier. Otherwise the source of
  // truth for tier is useSoloArtistSubscription, which differentiates
  // 'free' / 'booker' / 'pro' / 'shop_member' based on which Stripe
  // product the user is subscribed to.
  const tier: SoloArtistTier = isPlatformAdmin ? "pro" : subTier;

  const isFree = tier === "free";

  // Count appointments this month for the user
  const { data: appointmentsUsed = 0 } = useQuery({
    queryKey: ["solo-artist-appointments-count", user?.id],
    queryFn: async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      const { count, error } = await supabase
        .from("appointments")
        .select("id", { count: "exact", head: true })
        .eq("artist_id", user!.id)
        .gte("scheduled_at", startOfMonth)
        .lte("scheduled_at", endOfMonth);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user && isFree,
    staleTime: 30000,
  });

  // Count total clients created by this user (solo artists use their own shop)
  const { data: clientsUsed = 0 } = useQuery({
    queryKey: ["solo-artist-clients-count", user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("clients")
        .select("id", { count: "exact", head: true })
        .eq("created_by", user!.id);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user && isFree,
    staleTime: 30000,
  });

  const appointmentsLimit = isFree ? FREE_TIER_LIMITS.appointmentsPerMonth : Infinity;
  const clientsLimit = isFree ? FREE_TIER_LIMITS.totalClients : Infinity;

  const canCreateAppointment = !isFree || appointmentsUsed < FREE_TIER_LIMITS.appointmentsPerMonth;
  const canCreateClient = !isFree || clientsUsed < FREE_TIER_LIMITS.totalClients;

  const isProFeature = (feature: ProFeature): boolean => {
    if (tier === "pro" || tier === "shop_member") return false; // not locked
    // On free tier, these features are locked
    return true;
  };

  const PRO_FEATURES: ProFeature[] = ["sms", "ai_assistant", "finance", "pos", "events"];

  return {
    tier,
    isFree,
    isBooker: tier === "booker",
    isPro: tier === "pro",
    isProPlus: tier === "pro_plus",
    isShopMember: tier === "shop_member",
    canCreateAppointment,
    canCreateClient,
    appointmentsUsed: isFree ? appointmentsUsed : 0,
    appointmentsLimit,
    clientsUsed: isFree ? clientsUsed : 0,
    clientsLimit,
    isProFeature,
    PRO_FEATURES,
  };
}
